import { FC, useEffect, useRef } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
import { Box, CircularProgress } from '@mui/material';
import CreatedDatePicker from 'src/components/InputFields/DatePicker';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import { apiUrls, help, successMessages } from 'src/utils/properties.utils';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import { DeliveryUpdatePayload } from 'src/models/Management.interface';
import { UpdateDeliveryDialogProps } from 'src/models/UpdateDeliveryDialog.interface';
import {
  CloseIcon,
  StyledDialog,
} from 'src/components/DialogBox/DialogBox.styles';
import {
  ScrollableContent,
  FormContainer,
  FieldLabel,
  RadioGroup,
  RadioOption,
  RadioCircle,
  RadioLabel,
  FixedButtonContainer,
  UpdateButton,
  CancelButton,
  HeaderContainer,
  HeaderContent,
  AlertIcon,
  HeaderTitle,
  HeaderDivider,
  ErrorMessage,
  DialogContainer,
  FirstFieldGroup,
  DeliveryNoteFieldContainer,
  DeliveryStatusContainer,
  ActualDeliveryFieldGroup,
  DeliveryNoteLabel,
  DateLabel,
  SmallValue14,
} from './UpdateDeliveryDialog.styles';
import ExpandableText from 'src/components/ExpandableText/ExpandableText';

const UpdateDeliveryDialog: FC<UpdateDeliveryDialogProps> = ({
  open,
  onClose,
  rowId,
  data,
  onSuccess,
}) => {
  const { showSnackbar } = StyledSnackbar();
  const actualDeliveryDateRef = useRef<HTMLDivElement>(null);

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    initialValues: {
      scheduledDeliveryDate:
        data?.scheduledDeliveryDate || dayjs().format('YYYY-MM-DD'),
      deliveryNotes: data?.deliveryNote || '',
      isDelivered: Boolean(data?.isDelivered || data?.actualDeliveryDate),
      actualDeliveryDate: data?.actualDeliveryDate || null,
    },
    validationSchema: Yup.object().shape({
      isDelivered: Yup.boolean().required(),
      actualDeliveryDate: Yup.string().when(
        'isDelivered',
        ([isDelivered], schema) =>
          isDelivered
            ? schema
                .required(help.ACTUAL_DELIVERY_DATE_REQUIRED)
                .test(
                  'not-future',
                  help.ACTUAL_DELIVERY_DATE_FUTURE,
                  function (value) {
                    if (!value) return true;
                    const selectedDate = dayjs(value).startOf('day');
                    const today = dayjs().startOf('day');
                    return selectedDate.isSameOrBefore(today);
                  }
                )
            : schema.nullable()
      ),
    }),
    onSubmit: async (values) => {
      try {
        const payload: DeliveryUpdatePayload = {
          id: rowId,
          scheduledDeliveryDate: values.scheduledDeliveryDate
            ? dayjs(values.scheduledDeliveryDate).format('YYYY-MM-DD')
            : null,
          deliveryNotes: values.deliveryNotes || null,
          isDelivered: values.isDelivered,
          actualDeliveryDate: values.isDelivered
            ? values.actualDeliveryDate
              ? dayjs(values.actualDeliveryDate).format('YYYY-MM-DD')
              : null
            : null,
        };

        const response = await axiosTokenInstance.patch(
          apiUrls.UPDATE_ORDER_DELIVERY,
          payload
        );
        showSnackbar(
          response.data.message || successMessages.DELIVERY_DETAILS_UPDATED,
          'success'
        );
        onClose();
        onSuccess && onSuccess();
      } catch (error: unknown) {
        const errorMessage = await getApiErrorMessage(error);
        showSnackbar(errorMessage, 'error');
      }
    },
  });

  useEffect(() => {
    if (!open) {
      formik.resetForm();
    } else {
      // Mark fields as touched on open to show validation errors immediately
      formik.setFieldTouched('scheduledDeliveryDate', true);
      // If incoming data already has an actual delivery date, reflect that in the form
      if (data?.actualDeliveryDate) {
        formik.setFieldValue('isDelivered', true);
        formik.setFieldValue('actualDeliveryDate', data.actualDeliveryDate);
        formik.setFieldTouched('actualDeliveryDate', true);
      } else if (formik.values.isDelivered) {
        formik.setFieldTouched('actualDeliveryDate', true);
      }
    }
  }, [open]);

  useEffect(() => {
    if (formik.values.isDelivered && actualDeliveryDateRef.current) {
      setTimeout(() => {
        actualDeliveryDateRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }, 100);
      // Mark as touched when field becomes visible
      formik.setFieldTouched('actualDeliveryDate', true);
    } else {
      // Clear touched state and errors when not delivered
      formik.setFieldTouched('actualDeliveryDate', false);
      formik.setFieldError('actualDeliveryDate', undefined);
    }
  }, [formik.values.isDelivered]);

  // Trigger validation immediately when dates change
  useEffect(() => {
    if (formik.values.scheduledDeliveryDate) {
      formik.setFieldTouched('scheduledDeliveryDate', true, true);
    }
  }, [formik.values.scheduledDeliveryDate]);

  useEffect(() => {
    if (formik.values.actualDeliveryDate) {
      formik.setFieldTouched('actualDeliveryDate', true, true);
    }
  }, [formik.values.actualDeliveryDate]);

  return (
    <StyledDialog open={open} onClose={onClose} width="724px" height="540px">
      <DialogContainer>
        <HeaderContainer>
          <HeaderContent>
            <AlertIcon component="i" className="bx bx-error-circle" />
            <HeaderTitle>Update Delivery Details</HeaderTitle>
          </HeaderContent>
          <HeaderDivider />
          <CloseIcon
            component="i"
            className="bx bx-x-circle"
            onClick={onClose}
          />
        </HeaderContainer>
        <ScrollableContent>
          <FormContainer>
            <form onSubmit={formik.handleSubmit}>
              <FirstFieldGroup>
                <FieldLabel>Scheduled Delivery Date</FieldLabel>
                <DateLabel>
                  {data?.scheduledDeliveryDate
                    ? dayjs(data.scheduledDeliveryDate).format('DD MMM YYYY')
                    : 'N/A'}
                </DateLabel>
              </FirstFieldGroup>

              <DeliveryNoteFieldContainer>
                <FieldLabel>Delivery Note</FieldLabel>
                <SmallValue14>
                  <ExpandableText
                    text={data?.deliveryNote || 'N/A'}
                    lines={2}
                    placeholder="N/A"
                  />
                </SmallValue14>
              </DeliveryNoteFieldContainer>

              <DeliveryStatusContainer>
                <FieldLabel>Item delivered or not?</FieldLabel>
                <RadioGroup>
                  <RadioOption
                    selected={formik.values.isDelivered as boolean}
                    onClick={() => {
                      formik.setFieldValue('isDelivered', true);
                      if (!formik.values.actualDeliveryDate) {
                        formik.setFieldValue(
                          'actualDeliveryDate',
                          dayjs().format('YYYY-MM-DD')
                        );
                      }
                    }}
                  >
                    <RadioCircle
                      selected={formik.values.isDelivered as boolean}
                    />
                    <RadioLabel selected={formik.values.isDelivered as boolean}>
                      Delivered
                    </RadioLabel>
                  </RadioOption>
                  <RadioOption
                    selected={!(formik.values.isDelivered as boolean)}
                    onClick={() => formik.setFieldValue('isDelivered', false)}
                  >
                    <RadioCircle
                      selected={!(formik.values.isDelivered as boolean)}
                    />
                    <RadioLabel
                      selected={!(formik.values.isDelivered as boolean)}
                    >
                      Not delivered
                    </RadioLabel>
                  </RadioOption>
                </RadioGroup>
              </DeliveryStatusContainer>

              {formik.values.isDelivered && (
                <ActualDeliveryFieldGroup ref={actualDeliveryDateRef}>
                  <FieldLabel>Actual Delivery Date</FieldLabel>
                  <CreatedDatePicker
                    name="actualDeliveryDate"
                    formikProps={formik}
                    height="35px"
                    width="200px"
                    fontSize="14px"
                  />
                  {formik.touched.actualDeliveryDate &&
                    formik.errors.actualDeliveryDate && (
                      <ErrorMessage>
                        <Box component="i" className="bx bxs-error-circle" />
                        {formik.errors.actualDeliveryDate}
                      </ErrorMessage>
                    )}
                </ActualDeliveryFieldGroup>
              )}
            </form>
          </FormContainer>
        </ScrollableContent>
        <FixedButtonContainer>
          <UpdateButton
            type="button"
            onClick={() => formik.submitForm()}
            disabled={formik.isSubmitting}
          >
            {formik.isSubmitting ? (
              <CircularProgress size={16} sx={{ color: '#FFFFFF' }} />
            ) : (
              'Update'
            )}
          </UpdateButton>
          <CancelButton onClick={onClose}>Cancel</CancelButton>
        </FixedButtonContainer>
      </DialogContainer>
    </StyledDialog>
  );
};

export default UpdateDeliveryDialog;
